/**
 * Every string the recovery screen renders.
 *
 * The lede states where the screen is reached from, because this console has no
 * second entry into it: the account surfaces — settings, security, credential
 * change — all live on the subscriber host, and «الإعدادات» here is the runtime
 * configuration of the services rather than the account of whoever is looking.
 * Promising an entry point that does not exist in this area would send an
 * operator hunting for it.
 *
 * There is **no «current password» field** and no copy implying one. The code
 * is what proves identity here, and asking for the current password would shut
 * out exactly the person the screen exists for.
 */
export const copy = {
  documentTitle: 'إعادة تعيين كلمة المرور — وحدة تحكّم شاهدني',

  head: {
    title: 'إعادة تعيين كلمة المرور',
    lede: 'يصلك رمز تحقّق على رقم موبايلك، ثمّ تكتب الكلمة الجديدة مرّتين. الوصول إلى هذه الشاشة من شاشة الدخول؛ وتغيير كلمة المرور من حسابك يجري على مضيف المشترك، لا في وحدة التحكّم.',
  },

  /**
   * The identity step, drawn only when the screen was reached without a number —
   * a typed address, or the sign-in link pressed before the field was filled.
   * Both are ordinary ways in, so neither may dead-end.
   */
  phone: {
    label: 'رقم الموبايل',
    hint: 'الرقم المسجّل على الحساب. يُرسَل إليه رمز الاستعادة.',
    submit: 'أرسل رمز الاستعادة',
    sent: 'أُرسل رمز الاستعادة إلى رقمك. اكتبه أدناه مع كلمة المرور الجديدة.',
  },

  code: {
    label: 'رمز التحقّق',
    hint: 'الرمز نفسه الذي يصل عند الدخول — رسالة نصّية على رقم حسابك.',
  },

  password: {
    label: 'كلمة المرور الجديدة',
    hint: 'اختر كلمة لا تستعملها في مكان آخر.',
  },

  passwordConfirm: {
    label: 'تأكيد كلمة المرور',
    hint: 'اكتب الكلمة نفسها مرّة ثانية.',
  },

  actions: {
    save: 'احفظ كلمة المرور',
    offline: 'لا اتّصال — أعد المحاولة بعد عودة الشبكة',
  },

  /**
   * The disabled submit explains itself in adjacent text, and the line is
   * static rather than live: the condition does not change, only the button's
   * state does — and the button announces that itself.
   */
  note: 'يصير الحفظ متاحاً بعد إدخال الرمز وتطابق كلمتَي المرور.',

  /**
   * A password change revokes every live session on every host, so there is
   * nothing to walk into afterwards. The screen says so and points at the way
   * back rather than dropping the operator on a console that would bounce them.
   */
  saved: 'حُفظت كلمة المرور، وأُنهيت كلّ الجلسات المفتوحة. سجّل الدخول بالكلمة الجديدة.',

  foot: {
    remembered: 'تذكّرتَ كلمة المرور؟ عد إلى الدخول',
  },
} as const;
